import { Apollo } from 'src/gql/generated';

type SkipCount = { count: number; reason: Apollo.DocumentStatusSkipReason };

export type DocumentStatusJobBatch = Omit<
  Apollo.DocumentStatusJobBatch,
  '__typename' | 'skipCounts'
> & {
  skipCounts: readonly SkipCount[];
  __typename?: 'DocumentStatusJobBatch';
};

export type DocumentStatusJobPhase = 'cancelled' | 'cancelling' | 'done' | 'running';

export interface DocumentStatusJobProgress {
  numFailed: number;
  /** Documents whose chunk finished or that a cancel left alone, for the progress bar. */
  numFinished: number;
  numNotRun: number;
  numQueued: number;
  numSuccess: number;
  phase: DocumentStatusJobPhase;
  skipCounts: SkipCount[];
  status: Apollo.DocumentStatus;
}

/**
 * Merge batch totals from an event or a query into what the cache holds.
 * Every source carries a batch's whole running total, so the one furthest along
 * wins. That makes a repeated, late, or out-of-order update harmless.
 */
export function mergeDocumentStatusJobBatches<TBatch extends DocumentStatusJobBatch>(
  existing: readonly TBatch[],
  incoming: readonly TBatch[]
): TBatch[] {
  const byIndex = new Map(existing.map(batch => [batch.index, batch]));
  for (const batch of incoming) {
    const current = byIndex.get(batch.index);
    byIndex.set(batch.index, current ? pickFurtherBatch(current, batch) : batch);
  }
  return [...byIndex.values()].sort((a, b) => a.index - b.index);
}

/**
 * A job is finished once its known batches cover every queued document and
 * each of them is finished. Until then a batch not heard from yet counts as running.
 */
export function getDocumentStatusJobProgress(job: {
  batches: readonly DocumentStatusJobBatch[];
  numQueued: number;
  status: Apollo.DocumentStatus;
}): DocumentStatusJobProgress {
  const covered = sumBy(job.batches, batch => batch.numQueued);
  const isFinished = covered >= job.numQueued && job.batches.every(getIsBatchFinished);
  const wasCancelled = job.batches.some(
    batch =>
      batch.state === Apollo.DocumentStatusJobState.Cancelled ||
      batch.state === Apollo.DocumentStatusJobState.Cancelling
  );
  const numNotRun = sumBy(job.batches, batch => batch.numNotRun);
  const skipCounts = new Map<Apollo.DocumentStatusSkipReason, number>();
  for (const batch of job.batches) {
    for (const { count, reason } of batch.skipCounts)
      skipCounts.set(reason, (skipCounts.get(reason) ?? 0) + count);
  }

  return {
    numFailed: sumBy(job.batches, batch => batch.numFailed),
    numFinished: sumBy(job.batches, batch => batch.numProcessed) + numNotRun,
    numNotRun,
    numQueued: job.numQueued,
    numSuccess: sumBy(job.batches, batch => batch.numSuccess),
    phase: getPhase({ isFinished, wasCancelled }),
    skipCounts: [...skipCounts].map(([reason, count]) => ({ count, reason })),
    status: job.status,
  };
}

export function getIsDocumentStatusJobActive(phase: DocumentStatusJobPhase): boolean {
  return phase === 'running' || phase === 'cancelling';
}

/**
 * Running jobs always show. A finished job shows only in a tab that watched it
 * run, until dismissed, so a reload does not bring back a summary already seen.
 */
export function getIsDocumentStatusJobVisible(input: {
  dismissed: boolean;
  phase: DocumentStatusJobPhase;
  watched: boolean;
}): boolean {
  if (getIsDocumentStatusJobActive(input.phase)) return true;
  return input.watched && !input.dismissed;
}

function pickFurtherBatch<TBatch extends DocumentStatusJobBatch>(
  current: TBatch,
  incoming: TBatch
): TBatch {
  if (getIsBatchFinished(current)) return current;
  if (getIsBatchFinished(incoming)) return incoming;

  const further = incoming.numProcessed >= current.numProcessed ? incoming : current;
  const isCancelling =
    current.state === Apollo.DocumentStatusJobState.Cancelling ||
    incoming.state === Apollo.DocumentStatusJobState.Cancelling;
  return isCancelling ? { ...further, state: Apollo.DocumentStatusJobState.Cancelling } : further;
}

function getIsBatchFinished(batch: Pick<DocumentStatusJobBatch, 'state'>): boolean {
  return (
    batch.state === Apollo.DocumentStatusJobState.Done ||
    batch.state === Apollo.DocumentStatusJobState.Cancelled
  );
}

function getPhase(input: { isFinished: boolean; wasCancelled: boolean }): DocumentStatusJobPhase {
  if (input.isFinished) return input.wasCancelled ? 'cancelled' : 'done';
  return input.wasCancelled ? 'cancelling' : 'running';
}

function sumBy<TItem>(items: readonly TItem[], value: (item: TItem) => number): number {
  return items.reduce((sum, item) => sum + value(item), 0);
}
